import axios from 'axios';

// ============================================
// CONFIGURATION
// ============================================
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';
const BOM_API_BASE = `${API_BASE}/api/v1/manufacturing/bom`;

const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  },
});

// ============================================
// TYPES
// ============================================

export interface BOMComponent {
  id?: string;
  bom_id?: string;
  item_id: string;
  item_name?: string;
  item_code?: string;
  item_type?: string;
  quantity: number;
  scrap_percentage: number;
  unit_price?: number;
  total_price?: number;
  sequence_no?: number;
  unit_name?: string;
  qc_required?: boolean;
  notes?: string | null;
}

export interface BOM {
  id: string;
  code: string;
  product_id: string;
  product_name: string;
  product_code: string;
  product_type?: string;
  version: string;
  status: 'DRAFT' | 'APPROVED' | 'ACTIVE' | 'OBSOLETE';
  /** Components are entered for this many units of the product; quantities are stored per 1 unit */
  base_qty?: number | string;
  approved_by_name?: string | null;
  approved_at?: string | null;
  review_note?: string | null;
  is_variant_bom: boolean;
  variant_name: string | null;
  levels: number;
  total_items: number;
  total_material_cost: number;
  warehouse_id: string;
  warehouse_name?: string;
  link_to_item_master: boolean;
  effective_from: string;
  effective_to: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  company_id?: string;
  items?: BOMComponent[];
  summary?: {
    raw: number;
    semi: number;
    consumable: number;
    packaging: number;
    total: number;
    scrapCost: number;
  };
}

export interface BOMListResponse {
  success: boolean;
  data: BOM[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
}

export interface BOMCreateData {
  code?: string;
  productId: string;
  version: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isVariantBom?: boolean;
  variantName?: string | null;
  warehouseId: string;
  linkToItemMaster?: boolean;
  items: {
    itemId: string;
    quantity: number;
    scrapPercentage: number;
    sequenceNo?: number;
  }[];
}

export interface BOMUpdateData {
  version?: string;
  status?: 'DRAFT' | 'APPROVED' | 'ACTIVE' | 'OBSOLETE';
  effectiveFrom?: string;
  effectiveTo?: string | null;
  variantName?: string | null;
  warehouseId?: string;
  linkToItemMaster?: boolean;
  items?: {
    itemId: string;
    quantity: number;
    scrapPercentage: number;
    sequenceNo?: number;
  }[];
}

// ============================================
// API FUNCTIONS
// ============================================

export const createBOM = async (data: BOMCreateData): Promise<BOM> => {
  const response = await axios.post(BOM_API_BASE, data, getAuthHeaders());
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to create BOM');
  }
  return response.data.data;
};

export const getAllBOMs = async (params?: {
  status?: string;
  type?: 'REGULAR' | 'VARIANT';
  search?: string;
  warehouseId?: string;
  page?: number;
  limit?: number;
}): Promise<BOMListResponse> => {
  const queryParams = new URLSearchParams();

  if (params?.status && params.status !== 'ALL') {
    queryParams.append('status', params.status);
  }
  if (params?.type) {
    queryParams.append('type', params.type);
  }
  if (params?.search) {
    queryParams.append('search', params.search);
  }
  if (params?.warehouseId) {
    queryParams.append('warehouseId', params.warehouseId);
  }
  if (params?.page) {
    queryParams.append('page', String(params.page));
  }
  if (params?.limit) {
    queryParams.append('limit', String(params.limit));
  }

  const url = `${BOM_API_BASE}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axios.get(url, getAuthHeaders());
  return response.data;
};

export const getBOMById = async (id: string): Promise<BOM> => {
  const response = await axios.get(`${BOM_API_BASE}/${id}`, getAuthHeaders());
  if (!response.data.success) {
    throw new Error(response.data.message || 'BOM not found');
  }
  return response.data.data;
};

export const updateBOM = async (
  id: string,
  data: BOMUpdateData,
): Promise<BOM> => {
  const response = await axios.put(
    `${BOM_API_BASE}/${id}`,
    data,
    getAuthHeaders(),
  );
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to update BOM');
  }
  return response.data.data;
};

export const deleteBOM = async (id: string): Promise<void> => {
  const response = await axios.delete(
    `${BOM_API_BASE}/${id}`,
    getAuthHeaders(),
  );
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to delete BOM');
  }
};

export const duplicateBOM = async (id: string): Promise<BOM> => {
  const response = await axios.post(
    `${BOM_API_BASE}/${id}/duplicate`,
    {},
    getAuthHeaders(),
  );
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to duplicate BOM');
  }
  return response.data.data;
};

export const linkBOMToItemMaster = async (
  id: string,
): Promise<{ linkToItemMaster: boolean; standardCost: number }> => {
  const response = await axios.post(
    `${BOM_API_BASE}/${id}/link`,
    {},
    getAuthHeaders(),
  );
  if (!response.data.success) {
    throw new Error(
      response.data.message || 'Failed to link BOM to item master',
    );
  }
  return response.data.data;
};

// ✅ ADD THIS
export const unlinkBOMFromItemMaster = async (
  id: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await axios.post(
    `${BOM_API_BASE}/${id}/unlink`,
    {},
    getAuthHeaders(),
  );
  if (!response.data.success) {
    throw new Error(
      response.data.message || 'Failed to unlink BOM from item master',
    );
  }
  return response.data;
};

// ============================================
// DEFAULT EXPORT

export interface BOMExplosionItem {
  itemId: string;
  itemName: string;
  itemCode: string;
  itemType: string;
  unit: string;
  unitPrice: number;
  requiredQty: number;
  stockAvailable: number;
  diff: number;
  isShort: boolean;
  lineCost: number;
  level: number;
}

export interface BOMExplosionResponse {
  bom: {
    id: string;
    code: string;
    version: string;
    productName: string;
    productCode: string;
    warehouseId: string;
  };
  productionQty: number;
  items: BOMExplosionItem[];
  summary: {
    totalCost: number;
    shortageCount: number;
    sufficientCount: number;
    totalItems: number;
  };
}

export const getBOMExplosion = async (
  id: string,
  qty = 1,
  warehouseId?: string,
): Promise<BOMExplosionResponse> => {
  const url = warehouseId
    ? `${BOM_API_BASE}/${id}/explosion?qty=${qty}&warehouse_id=${encodeURIComponent(warehouseId)}`
    : `${BOM_API_BASE}/${id}/explosion?qty=${qty}`;
  const response = await axios.get(
    url,
    getAuthHeaders(),
  );
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to fetch BOM explosion');
  }
  return response.data.data;
};

export const getNextBOMCode = async (): Promise<string> => {
  const response = await axios.get<{ success: boolean; data: { code: string } }>(
    `${API_BASE}/next-code`,
    getAuthHeaders(),
  );
  if (!response.data.success) {
    throw new Error('Failed to fetch next BOM code');
  }
  return response.data.data.code;
};

// ============================================

const bomAPI = {
  create: createBOM,
  getAll: getAllBOMs,
  getById: getBOMById,
  update: updateBOM,
  delete: deleteBOM,
  duplicate: duplicateBOM,
  linkToItemMaster: linkBOMToItemMaster,
  unlinkFromItemMaster: unlinkBOMFromItemMaster,
  getNextCode: getNextBOMCode,
};

export default bomAPI;
